import { useForm } from '@tanstack/react-form'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { toast } from 'sonner'
import { Minus, Plus } from 'lucide-react'

import type { Event } from '@/gen/cortado/v1/inquiries_pb'
import { CreateEventRequestSchema, UpdateEventRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { EventStageEnum } from '@/gen/cortado/v1/settings_pb'
import { useCreateEvent, useUpdateEvent, useResolveLocationTimezone } from '@/stores/events.store'
import { useBooking } from '@/stores/bookings.store'
import { dateToTimestamp } from '@/lib/date-utils'
import {
  timestampToDate,
  formatTimestampToTimeString,
  calculateServiceTimeRange,
  MINUTE_IN_HOURS,
} from '@/lib/inquiry-utils'
import { useEventTypes } from '@/hooks/use-event-types'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { cn } from '@/lib/utils'
import { InputGroupInput } from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DurationInput } from '@/components/shared/duration-input'
import { DatePicker } from '@/components/shared/date-picker'
import { TimePicker } from '@/components/shared/time-picker'
import { BaristaCartCountFields } from '@/components/shared/barista-cart-count-fields'
import { TimezoneSelect } from '@/components/shared/timezone-select'
import { LocationTimezoneSync } from '@/components/shared/location-timezone-sync'
import { EventStageBadge } from '@/components/shared/enum-badge'
import { PlacesAutocomplete } from '@/components/ui/places-autocomplete'
import { InputGroup } from '@/components/ui/input-group'

const EVENT_STAGE_OPTIONS = [
  { value: EventStageEnum.EVENT_STAGE_UPCOMING, label: 'Upcoming' },
  { value: EventStageEnum.EVENT_STAGE_COMPLETE, label: 'Complete' },
  { value: EventStageEnum.EVENT_STAGE_CANCELLED, label: 'Cancelled' },
  { value: EventStageEnum.EVENT_STAGE_RESCHEDULED, label: 'Rescheduled' },
  { value: EventStageEnum.EVENT_STAGE_POSTPONED, label: 'Postponed' },
] as const

interface EventSheetProps {
  mode: 'create' | 'update'
  bookingId: string
  event?: Event
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
  /** Which field takes focus when the sheet opens; the Directions step opens on the location. */
  initialFocus?: 'location'
}

/**
 * EventSheet - Reusable sheet component for creating and updating events
 *
 * Features:
 * - Create new events within a booking with date/time, duration, and guest count
 * - Update existing event date/time, duration, and guest count
 * - Uses TanStack Form for form state management
 * - Leverages useCreateEvent/useUpdateEvent hooks for optimized cache updates
 * - Displays loading states during mutation
 * - Shows toast notifications on success
 * - Resets form on close
 *
 * @param mode - 'create' or 'update' mode
 * @param bookingId - The booking ID to create the event for (required for create)
 * @param event - The event to edit (required for update mode)
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function EventSheet({
  mode,
  bookingId,
  event,
  open,
  onOpenChange,
  layout,
  initialFocus,
}: EventSheetProps) {
  const { mutation: createMutation } = useCreateEvent()
  const { mutation: updateMutation } = useUpdateEvent()
  const { resolve: resolveTimezoneFromLocation } = useResolveLocationTimezone()
  const { booking } = useBooking(bookingId)

  // Get the mutation based on mode
  const mutation = mode === 'create' ? createMutation : updateMutation

  // Archived types are not assignable to new records, but the one already
  // on this record stays selectable so the field isn't blank.
  const { getEventTypeOptions } = useEventTypes()

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      eventDate: mode === 'create' ? undefined : timestampToDate(event?.eventDate) as Date | undefined,
      eventTime: mode === 'create' ? '' : formatTimestampToTimeString(event?.eventDate),
      eventLength: mode === 'create' ? 0.5 : (event?.eventLength ?? 0.5),
      eventGuests: mode === 'create' ? 0 : (event?.eventGuests ?? 0),
      baristaCount: mode === 'create' ? 1 : (event?.baristaCount ?? 1),
      cartCount: mode === 'create' ? 1 : (event?.cartCount ?? 1),
      stage: mode === 'create' ? EventStageEnum.EVENT_STAGE_UPCOMING : (event?.stage ?? EventStageEnum.EVENT_STAGE_UPCOMING),
      eventType: mode === 'create'
		? (booking?.eventTypeKey ?? '')
		: (event?.eventTypeKey ?? ''),
      // The event owns its location — never seed it from the booking, or saving
      // any other field would silently copy the booking's address onto the event.
      eventLocation: mode === 'create' ? '' : (event?.eventLocation ?? ''),
      eventTimezone: mode === 'create'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : (event?.eventTimezone ?? ''),
    },
    onSubmit: async ({ value }) => {
      try {
        // Combine date and time into a Date object
        let eventDate: Date | undefined
        if (value.eventDate && value.eventTime) {
          const timeParts = value.eventTime.split(':')
          const hours = parseInt(timeParts[0], 10)
          const minutes = parseInt(timeParts[1], 10)
          eventDate = new Date(value.eventDate)
          eventDate.setHours(hours, minutes, 0, 0)
        } else if (value.eventDate) {
          eventDate = new Date(value.eventDate)
          eventDate.setHours(0, 0, 0, 0)
        }

        if (mode === 'create') {
          // Build CreateEventRequest
          const request = create(CreateEventRequestSchema, {
            bookingId,
            eventDate: eventDate ? dateToTimestamp(eventDate) : undefined,
            eventLength: value.eventLength > 0 ? value.eventLength : undefined,
            eventGuests: value.eventGuests >= 0 ? value.eventGuests : undefined,
            baristaCount: value.baristaCount ?? undefined,
            cartCount: value.cartCount ?? undefined,
            eventTypeKey: value.eventType || undefined,
            eventLocation: value.eventLocation.trim() || undefined,
            eventTimezone: value.eventTimezone || undefined,
          })

          await createMutation.mutateAsync(request)
          toast.success('Event created successfully')
        } else {
          // Build UpdateEventRequest
          if (!event) {
            toast.error('No event to update')
            return
          }

          const request = create(UpdateEventRequestSchema, {
            id: event.id,
            eventDate: eventDate ? dateToTimestamp(eventDate) : undefined,
            eventLength: value.eventLength > 0 ? value.eventLength : undefined,
            eventGuests: value.eventGuests >= 0 ? value.eventGuests : undefined,
            baristaCount: value.baristaCount,
            cartCount: value.cartCount,
            stage: value.stage,
            eventTypeKey: value.eventType,
            // Sent even when empty so clearing the location persists.
            eventLocation: value.eventLocation.trim(),
            eventTimezone: value.eventTimezone || undefined,
            updateMask: create(FieldMaskSchema, {
				paths: ['event_date', 'event_length', 'event_guests', 'barista_count', 'cart_count', 'stage', 'event_type_key', 'event_location', 'event_timezone'],
            }),
          })

          await updateMutation.mutateAsync(request)
          toast.success('Event updated successfully')
        }

        onOpenChange(false)
        form.reset()
      } catch (error) {
        console.error(`Failed to ${mode} event:`, error)
        reportError(error, { feature: `event_${mode}`, kind: 'mutation' })
        toast.error(`Failed to ${mode} event`, {
          description: getErrorDescription(error),
        })
      }
    },
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(layout === 'pipeline' && 'w-full sm:max-w-xl')}
        onOpenAutoFocus={(e) => {
          if (initialFocus !== 'location') return
          const field = document.getElementById('event-location')
          if (!field) return
          e.preventDefault()
          field.focus()
          field.scrollIntoView?.({ block: 'center' })
        }}
      >
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Add Event' : 'Edit Event'}</SheetTitle>
          <SheetDescription>
            {mode === 'create'
              ? 'Create a new event within this booking.'
              : 'Make changes to the event information. Click save when you\'re done.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="event-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="eventDate">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-date">Event Date</Label>
                  <DatePicker
                    value={field.state.value}
                    onChange={(date) => field.handleChange(date)}
                    disabled={mutation.isPending}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="eventTime">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="event-time">Event Time</Label>
                  <TimePicker
                    value={field.state.value}
                    onChange={(time) => field.handleChange(time)}
                    disabled={mutation.isPending}
                    placeholder="Select time"
                  />
                </div>
              )}
            </form.Field>
          </div>

          <form.Subscribe selector={(s) => s.values.eventLocation}>
            {(eventLocation) => (
              <form.Field name="eventTimezone">
                {(field) => {
                  const trimmedLocation = (eventLocation ?? '').trim()
                  return (
                    <div className="grid gap-1">
                      <TimezoneSelect
                        id="event-timezone"
                        label="Timezone"
                        value={field.state.value || undefined}
                        onChange={(value) => field.handleChange(value)}
                        disabled={mutation.isPending}
                        onResolveFromLocation={() => resolveTimezoneFromLocation(trimmedLocation)}
                        resolveDisabledReason={trimmedLocation ? undefined : 'Set an event location first'}
                      />
                      <p className="text-xs text-muted-foreground">
                        Follows the event location automatically. Pick a zone here to override it.
                      </p>
                    </div>
                  )
                }}
              </form.Field>
            )}
          </form.Subscribe>

          <form.Field name="eventLength">
            {(field) => {
              const serviceTime = calculateServiceTimeRange(
                form.state.values.eventTime,
                field.state.value
              )

              return (
                <div className="grid gap-3">
                  <Label htmlFor="event-length">Duration</Label>
                  <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                    <DurationInput
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      disabled={mutation.isPending}
                      min={MINUTE_IN_HOURS}
                      step={0.25}
                    />
                  </div>
                  {serviceTime && (
                    <p className="text-sm text-muted-foreground px-1">
                      {serviceTime}
                    </p>
                  )}
                </div>
              )
            }}
          </form.Field>

          <form.Field name="eventGuests">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="event-guests">Guest Count</Label>
                <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                  <div className="flex border-r border-input">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newValue = Math.max(0, field.state.value - 25)
                        field.handleChange(newValue)
                      }}
                      disabled={mutation.isPending || field.state.value <= 0}
                    >
                      <Minus className="h-4 w-4" />
                      <span className="sr-only">Decrease by 25</span>
                    </Button>
                  </div>
                  <InputGroupInput
                    id="event-guests"
                    type="number"
                    min="0"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                    onBlur={(e) => {
                      const value = Number(e.target.value) || 0
                      // Enforce minimum of 0
                      const clampedValue = Math.max(0, value)
                      field.handleChange(clampedValue)
                      field.handleBlur()
                    }}
                    disabled={mutation.isPending}
                    placeholder="0"
                    className="text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="flex border-l border-input">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newValue = field.state.value + 25
                        field.handleChange(newValue)
                      }}
                      disabled={mutation.isPending}
                    >
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Increase by 25</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </form.Field>

          <form.Field name="baristaCount">
            {(baristaField) => (
              <form.Field name="cartCount">
                {(cartField) => (
                  <BaristaCartCountFields
                    baristaCount={baristaField.state.value}
                    cartCount={cartField.state.value}
                    onBaristaCountChange={baristaField.handleChange}
                    onCartCountChange={cartField.handleChange}
                    onBaristaCountBlur={baristaField.handleBlur}
                    onCartCountBlur={cartField.handleBlur}
                    disabled={mutation.isPending}
                  />
                )}
              </form.Field>
            )}
          </form.Field>

          <form.Field name="eventType">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="event-type">Event Type</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  disabled={mutation.isPending}
                >
                  <SelectTrigger id="event-type" className="w-full">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {getEventTypeOptions(field.state.value).map((eventType) => (
                      <SelectItem key={eventType.key} value={eventType.key}>
                        {eventType.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="eventLocation">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="event-location">Location</Label>
                <InputGroup>
                  <PlacesAutocomplete
                    id="event-location"
                    autoFocus={initialFocus === 'location'}
                    value={field.state.value}
                    onChange={(value) => field.handleChange(value)}
                    onBlur={field.handleBlur}
                    placeholder="Enter event location"
                    disabled={mutation.isPending}
                  />
                </InputGroup>
                <form.Subscribe selector={(s) => s.values.eventTimezone}>
                  {(eventTimezone) => (
                    <LocationTimezoneSync
                      location={field.state.value ?? ''}
                      timezone={eventTimezone ?? ''}
                      onTimezoneChange={(timezone) => form.setFieldValue('eventTimezone', timezone)}
                      resolve={resolveTimezoneFromLocation}
                    />
                  )}
                </form.Subscribe>
              </div>
            )}
          </form.Field>

          {mode === 'update' && (
            <form.Field name="stage">
              {(field) => (
                <div className="grid gap-3 col-span-full">
                  <Label htmlFor="event-stage">Stage</Label>
                  <Select
                    value={String(field.state.value)}
                    onValueChange={(value) => field.handleChange(Number(value))}
                    disabled={mutation.isPending}
                  >
                    <SelectTrigger id="event-stage" className="w-full">
                      <SelectValue placeholder="Select stage">
                        <EventStageBadge stage={field.state.value} />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_STAGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          <EventStageBadge stage={option.value} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          </form>
        </SheetBody>
        <SheetFooter className={cn(layout === 'pipeline' && 'sm:justify-between')}>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </SheetClose>
          <Button type="submit" form="event-form" disabled={mutation.isPending}>
            {mutation.isPending
              ? (mode === 'create' ? 'Creating...' : 'Saving...')
              : (mode === 'create' ? 'Create Event' : 'Save Changes')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
